/* eslint-disable no-console */
import { isOneTrustConfigured } from '@jjj/components';
import {
  isDayforce as isDayforceApp,
  isBench as isBenchApp,
  isMaof as isMaofApp,
  getTranslationsRootKey,
  getOrganizationCode,
  APP_NAME,
} from './organizationSettings';
import { LOCAL_SETTINGS_KEY } from '../helpers/localStorageKeys.const';

const {
  REACT_APP_ENVIRONMENT,
  REACT_APP_DASH_VMS_INTERCOM_CHAT_APP_ID,
  REACT_APP_INTERCOM_CHAT_APP_ID,
  REACT_APP_DEV_API_URL,
  REACT_APP_DASH_WS_URL,
  REACT_APP_DASH_STRIPE_PUBLISHABLE_KEY,
  REACT_APP_DATA_DOG_APPLICATION_ID,
  REACT_APP_DATA_DOG_CLIENT_TOKEN,
  REACT_APP_DASH_MAOF_STRIPE_PUBLISHABLE_KEY,
  REACT_APP_LOGIN_API_URL,
  REACT_APP_LOGIN_URL,
  REACT_APP_ONE_TRUST_DOMAIN_ID,
  // @ts-ignore
} = import.meta.env;

// The OneTrust SDK is only in the page when a domain script is configured at build time — the
// Vite plugin strips the notice otherwise. Checking the build value rather than `window.OneTrust`
// avoids racing the SDK, which defines its API only once the banner script has loaded.
// `isOneTrustConfigured` is the same predicate the plugin uses, so the two cannot disagree.
export const isOneTrustEnabled = isOneTrustConfigured(REACT_APP_ONE_TRUST_DOMAIN_ID);

export const PRIVACY_POLICY_URL = 'https://www.dayforce.com/privacy';
export const COOKIE_POLICY_URL = 'https://www.dayforce.com/privacy/cookie-notice';

export function openCookieSettings() {
  window.OneTrust?.ToggleInfoDisplay?.();
}

// Base domain for Dayforce Flex Work URLs
const DAYFORCE_BASE_DOMAIN = 'dayforceflexwork';

const SUPPORTED_TLDS = {
  UK: 'uk',
  COM: 'com',
} as const;

const ENV_SUBDOMAINS = {
  DEV: 'dev',
  STAGING: 'staging',
} as const;

export const defaultApiVersionPath = '/api/v1';

const intercomChatConfig = {
  APP_ID: REACT_APP_INTERCOM_CHAT_APP_ID,
  VMS_APP_ID: REACT_APP_DASH_VMS_INTERCOM_CHAT_APP_ID,
};

export const isBench = isBenchApp;
export const isMaof = isMaofApp;
export const isDayforce = isDayforceApp;

export const getIsNetworkProduct = () => {
  const code = getOrganizationCode();

  return code !== APP_NAME.dayforce_flex_work || code !== APP_NAME.maof;
};

export const translationsRootKey = getTranslationsRootKey();

const getStripeKey = () => {
  if (isMaof()) {
    return REACT_APP_DASH_MAOF_STRIPE_PUBLISHABLE_KEY;
  }

  if (isDayforce()) {
    return REACT_APP_DASH_STRIPE_PUBLISHABLE_KEY;
  }

  return '';
};

// `publishableKey` is a getter so it reflects the active org after unified-login sync.
const stripe = {
  get publishableKey() {
    return getStripeKey();
  },
};

const payrollImportFileConfig = { validFileTypes: ['text/csv', 'application/vnd.ms-excel'] };

const csvImportFileConfig = { validFileTypes: ['text/csv', 'application/vnd.ms-excel'] };

const payrollPaystubFileConfig = { validFileTypes: ['application/pdf'] };

const tableConfig = {
  pageSize: 10,
  maxPageSize: 50,
  virtualTablePageSize: 100,
  unlimited: 9999,
};

export const filesConfig = {
  imageMaxSize: 2, // 2MB
  documentMaxSize: 10, // 10MB
};

const fakeSignInData = {
  code: 'fakeToken',
  profileObj: {
    email: 'dfw.review.app.admin@dayforce.com',
    familyName: 'Test',
    givenName: 'DFW',
    googleId: 'fakeToken',
    imageUrl: 'nophoto',
    name: 'Test DFW',
  },
};

export {
  csvImportFileConfig,
  fakeSignInData,
  intercomChatConfig,
  payrollImportFileConfig,
  payrollPaystubFileConfig,
  tableConfig,
  stripe,
};

export const isDemo = REACT_APP_ENVIRONMENT === 'demo';
/** @public Cross-repo consistency with flex-web, ondemand-web */
export const isDev = REACT_APP_ENVIRONMENT === 'dev';
export const isProduction = REACT_APP_ENVIRONMENT === 'production';
export const isReview = REACT_APP_ENVIRONMENT === 'r';
export const isStaging = REACT_APP_ENVIRONMENT === 'staging';
export const isDevOrReview = isDev || isReview;
export const isDevOrReviewOrStagingOrDemo = isDevOrReview || isStaging || isDemo;
// Excludes demo and production on purpose: the backend refuses destructive calls there, so the control would only 403.
export const isDevOrReviewOrStaging = isDevOrReview || isStaging;
const isUkEnvironment = window.location.hostname.endsWith(`.${SUPPORTED_TLDS.UK}`);

export const getIsIntercomEnabled = () => isDemo || ((isProduction || isStaging) && !isMaof() && !isDayforce());

const settings = {
  apiUrl: REACT_APP_DEV_API_URL || 'https://dashapi.dev.jitjatjo.com',
  wsUrl: REACT_APP_DASH_WS_URL,
  loginApiUrl: REACT_APP_LOGIN_API_URL,
  loginUrl: REACT_APP_LOGIN_URL,
  organizationCode: 'dayforce_flex_work',
  appEnv: REACT_APP_ENVIRONMENT,
  dataDogApplicationId: REACT_APP_DATA_DOG_APPLICATION_ID,
  dataDogClientToken: REACT_APP_DATA_DOG_CLIENT_TOKEN,
  isDemo,
  isDev,
  isProduction,
  isReview,
  isStaging,
  isDevOrReview,
  isDevOrReviewOrStagingOrDemo,
};

// Extend the Window interface to include getProcessEnv
declare global {
  interface Window {
    getProcessEnv?: () => void;
  }
}

if (settings.isDevOrReview && window) {
  // eslint-disable-next-line no-console
  // @ts-ignore
  window.getProcessEnv = () => console.log('🚀 ~ file: index.js:290 ~ process.env:', import.meta.env);
}

export const getApiHost = () => {
  const localSettings = localStorage.getItem(LOCAL_SETTINGS_KEY);
  const appSettings = localSettings ? JSON.parse(localSettings) : settings;
  const url = new URL(appSettings.apiUrl);

  return url.origin;
};

// Rest API url
export const getApiUrl = (versionPath = defaultApiVersionPath) => {
  return `${getApiHost()}${versionPath}` || '';
};

// WebSocket url
export const getWsUrl = () => {
  const localSettings = localStorage.getItem(LOCAL_SETTINGS_KEY);
  const appSettings = localSettings ? JSON.parse(localSettings) : settings;

  return appSettings.wsUrl;
};

type ProductSubdomain = 'login' | 'admin' | 'app' | 'agency';

const getDayforceFlexWorkTld = (): string => {
  if (isUkEnvironment) {
    return SUPPORTED_TLDS.UK;
  }

  return SUPPORTED_TLDS.COM;
};

const buildDayforceUrl = (product: ProductSubdomain): string => {
  if (isDemo) {
    return `https://${product}.${DAYFORCE_BASE_DOMAIN}.app`;
  }

  const tld = getDayforceFlexWorkTld();

  if (isDevOrReview) {
    return `https://${product}.${ENV_SUBDOMAINS.DEV}.${DAYFORCE_BASE_DOMAIN}.${tld}`;
  }

  if (isStaging) {
    return `https://${product}.${ENV_SUBDOMAINS.STAGING}.${DAYFORCE_BASE_DOMAIN}.${tld}`;
  }

  // Production - no env subdomain
  return `https://${product}.${DAYFORCE_BASE_DOMAIN}.${tld}`;
};

export const getLoginApiUrl = () => {
  return REACT_APP_LOGIN_API_URL as string;
};

export const getUnifiedLoginUrl = () => {
  return REACT_APP_LOGIN_URL || buildDayforceUrl('login');
};

export const getOndemandUrl = () => {
  return buildDayforceUrl('app');
};

export const getAgencyUrl = () => {
  return buildDayforceUrl('agency');
};

export default settings;
